// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/ChatMessage.test.tsx
import {
  type AskPunttMessage,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { redactStorageLinks } from "@mg/schemas/src/christo/warholToolReturn";
import { type ReviewFiles } from "@mg/schemas/src/commons";
import {
  ArrowSquareOut,
  Globe,
  ThumbsDown,
  ThumbsUp,
} from "@phosphor-icons/react";
import { Flex, Text, Tooltip } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "react-i18next";

import { AgentSteps } from "./AgentSteps";
import { AssistantAttachments } from "./AssistantAttachments";
import { ChatVideoAttachment } from "./ChatVideoAttachment";
import { FileAttachmentButton } from "./FileAttachmentButton";
import { ImageWithLoader } from "./ImageWithLoader";
import { PunttAvatar } from "./PunttAvatar";
import {
  type QuestionAttachment,
  type QuestionFormHandle,
  QuestionForm,
} from "./QuestionForm";
import { QuestionFormAnswer } from "./QuestionFormAnswer";
import { SearchProgress } from "./SearchProgress";
import { StructuredResponse } from "./StructuredResponse";
import { ThinkingState } from "./ThinkingState";

import {
  AvatarWithInitials,
  AVATAR_SIZE_BIG,
  AVATAR_SIZE_NORMAL,
} from "../../../components/AvatarWithInitials";
import { Expandable } from "../../../components/Expandable";
import { knowledgeRuleCitationLabel } from "../../../components/KnowledgeRuleCitationDialog";
import { Markdown } from "../../../components/Markdown";
import { PunttThinking } from "../../../components/PunttThinking";
import { type LinkableClaim } from "../../../plugins/rehype-claim-citation";
import { useAnalytics } from "../../../utils/analytics";
import { useSubmitAskPunttFeedback } from "../../../utils/queries/askPuntt";
import { citationAnchorParam } from "../../settings/routes/knowledge-database/citationAnchor";
import { KNOWLEDGE_DATABASE_DEFAULT_SEARCH } from "../../settings/routes/knowledge-database/route";
import { KNOWLEDGE_DATABASE_PATH } from "../../settings/routes/knowledge-database/search";
import {
  CHAT_ATOM_AGENT_COMPONENT_IDS,
  chatAtomAgentInstanceId,
} from "../agentComponents/chatAtomRegistry";
import {
  ChatAtoms,
  collectDrawableChatAtoms,
} from "../agentComponents/ChatAtoms";
import {
  MENTION_HIGHLIGHT_CLASS,
  renderMentionSegments,
  resolveMentionActorsForFeedMessage,
} from "../chatUtils";
import {
  dedupeWebSourceCitations,
  isKnowledgeRuleCitation,
  isWebSourceCitation,
} from "../utils/commentSourceUtils";
import {
  computeRawDisplaySegments,
  stabilizeDisplaySegments,
} from "../utils/displaySegments";
import { formatFullDateTime } from "../utils/messageDateGrouping";
import { sanitizeStreamingMarkdown } from "../utils/streamingMarkdown";
import { useMessageClaimCitations } from "../utils/useMessageClaimCitations";

/** Short "5:04 PM" timestamp for the feed presentation header. */
function formatFeedTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

interface ChatMessageProps {
  message: AskPunttMessage;
  /** Chat ID for feedback submission */
  chatId?: string;
  /**
   * Ticket review files for this chat's active ticket. When present,
   * video attachments play from the same HLS playlist the ticket uses
   * (`reviewFiles[0].source`) because the raw S3 upload URL is revoked
   * by the ingest pipeline once transcoding finishes.
   */
  reviewFiles?: ReviewFiles[];
  /**
   * Map of attachment S3 key → browser blob URL pointing at the
   * original local file. When present, previews for freshly-uploaded
   * video/image attachments render straight from disk, avoiding the
   * CDN entirely. Only populated for messages sent in the current
   * session; rehydrated messages after a page refresh fall back to
   * the HLS/remote resolution logic.
   */
  localPreviewUrls?: Record<string, string>;
  /** True when the backend is still generating this message */
  isStreaming?: boolean;
  /** True when this is the last assistant message (enables interactive form) */
  isLastAssistant?: boolean;
  /** Called when user submits answers via the multi-question form */
  onSubmitAnswers?: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
  /** Disable the question form (e.g. while sending) */
  formDisabled?: boolean;
  /** Ref forwarded to the active QuestionForm for external submit triggers */
  questionFormRef?: React.Ref<QuestionFormHandle>;
  /** Called when the form's "has any answer" state changes */
  onAnswerChange?: (hasAnswer: boolean) => void;
  /**
   * When true, suppresses the inline QuestionForm render (it's shown in the
   * input area instead). Non-last messages still render read-only as usual.
   */
  hideInlineQuestionForm?: boolean;
  /**
   * When true, this user message is the answer to a prior `userQuestions`
   * form — render QuestionFormAnswer chips instead of the markdown bubble.
   * why: completed state lives in the feed/chat list after submit, not in the composer.
   */
  isQuestionFormAnswer?: boolean;
  /**
   * Undo handler for QuestionFormAnswer chips. When provided, each chip shows
   * the Figma Undo control (49858:1755).
   */
  onUndoQuestionFormAnswer?: () => void;
  /**
   * When the Deep review progress bar is live, hide leftover AgentSteps /
   * ThinkingState so "Create Asset" is not competing with the stage spine.
   */
  hideAgentChrome?: boolean;
  /**
   * True when the preceding user message carried file attachments. Forwarded
   * to ThinkingState so the pre-agent read pause shows "Reading attachments..."
   * instead of the generic "Starting up...".
   */
  precedingUserHasAttachments?: boolean;
  /**
   * Visual frame for the message. "chat" (default) is the classic chat layout
   * (right-aligned user bubble, left assistant body). "feed" renders a
   * Slack-style row — left avatar + name + role + timestamp header with a
   * left-aligned body — for the Ask Puntt Projects feed, while keeping all the
   * live machinery (streaming, agent steps, canvas, attachments) intact.
   */
  presentation?: "chat" | "feed";
  /**
   * Render the "AI Reviewer" role label beside Puntt's name in the feed
   * header. Opt-in: the V1 Projects feed is multi-participant, so readers
   * need AI turns attributed; the V2 project page follows the Figma frame
   * (49613:1111), which is Name + time only.
   */
  showFeedRoleLabel?: boolean;
  /** Display name for USER turns in the feed presentation (e.g. the viewer). */
  feedActorName?: string;
  /** Avatar URL for USER turns in the feed presentation. */
  feedActorAvatar?: string | null;
  /**
   * When true in feed presentation, the current viewer's user message gets the
   * white bordered bubble. Other members' messages use the gray bubble.
   */
  feedOwnUserMessage?: boolean;
  /**
   * Project members for the feed presentation, used to resolve mention ids on
   * a user message to display names when highlighting `@Name` chips.
   */
  feedMembers?: {
    _id: string;
    name?: string;
    email?: string;
    avatar?: string | null;
  }[];
  /**
   * Feed-only. When true, the message body is collapsed to a short clamped
   * preview with a "Show more" / "Show less" toggle. Set for every feed message
   * except the most recent (which stays fully expanded as the current focus).
   */
  collapsible?: boolean;
  /**
   * Feed-only. When true (narrow chat column), use an inline avatar+name header
   * instead of a full left avatar column so message text can use full width.
   */
  feedCompactLayout?: boolean;
  /**
   * Feed-only. When false, omit the avatar/name header (continuation in a
   * consecutive-author span). Defaults to true.
   */
  showAuthorHeader?: boolean;
  /**
   * Called when the user clicks a numbered Knowledge Database citation chip
   * under an assistant answer. Parents open / focus the Cited Claims rail.
   */
  onCitationSelect?: (selection: { messageId: string; ruleId: string }) => void;
  /** Currently selected rule id (highlights the matching numbered chip). */
  selectedCitationRuleId?: string | null;
  /** Rendered above the user message body, grouped in the same alignment column. */
  renderBeforeContent?: ReactNode;
  /** Rendered below the user message body, grouped in the same alignment column. */
  renderAfterContent?: ReactNode;
}

function chatMessageTestIds(messageId: string) {
  return {
    assistant: `chat-message-assistant-${messageId}`,
    user: `chat-message-user-${messageId}`,
    content: `chat-message-content-${messageId}`,
    feedRole: `feed-message-role-${messageId}`,
    textSegment: (segmentIdx: number) =>
      `chat-message-text-segment-${messageId}-${segmentIdx}`,
  } as const;
}

/**
 * Pulls answer labels from QuestionForm's `**Header:** answer` submit format.
 * Falls back to the full content when the message is not in that shape.
 */
export function parseQuestionFormAnswerLabels(content: string): string[] {
  const lines = content
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const labels: string[] = [];
  for (const line of lines) {
    const match = /^\*\*(.+?):\*\*\s*(.*)$/.exec(line);
    if (!match) continue;
    const answer = match[2]?.trim();
    if (answer) labels.push(answer);
  }
  return labels.length > 0 ? labels : content.trim() ? [content.trim()] : [];
}

/**
 * True when a text segment should use the memoized Markdown child.
 * Trailing incomplete streaming segments stay on live Markdown so sanitize
 * can update each tick; completed segments skip re-parse.
 */
export function shouldMemoizeMarkdownSegment(opts: {
  isStreaming: boolean;
  hasContent: boolean;
  isLastSegment: boolean;
}): boolean {
  return !(opts.isStreaming && !opts.hasContent && opts.isLastSegment);
}

/**
 * Memoized markdown for a completed text segment. Content is stable once a
 * segment is no longer the trailing streaming segment, so ReactMarkdown +
 * remark-gfm only re-parse the active trailing segment during a stream.
 */
const MemoizedMarkdownSegment = memo(function MemoizedMarkdownSegment({
  content,
  claimCitations,
  claimCitationScope,
}: {
  content: string;
  claimCitations?: LinkableClaim[];
  claimCitationScope?: string;
}) {
  return (
    <Markdown
      size="3"
      iconizeEmoji
      claimCitations={claimCitations}
      claimCitationScope={claimCitationScope}
    >
      {content}
    </Markdown>
  );
});

function agentStepsSignature(
  steps: AskPunttMessage["agentSteps"] | undefined,
): string {
  if (!steps?.length) return "";
  return `${steps.length}:${steps[steps.length - 1]?.type ?? ""}:${
    steps[steps.length - 1]?.timestamp ?? ""
  }`;
}

/**
 * A value signature, like the citations below: every poll rebuilds the array,
 * and the atoms land on an answer whose text is already final, so identity
 * would either re-render every tick or swallow the update that draws them.
 */
function componentsSignature(
  components: AskPunttMessage["components"] | undefined,
): string {
  if (!components?.length) return "";
  return components.map((c) => `${c.componentId}:${c.instanceId}`).join(",");
}

/**
 * Only KDB-rule and safe web variants of `citations` are rendered, so the memo
 * signature keys on the values that affect those two source rows.
 * A value signature — not identity — because every poll rebuilds the array:
 * comparing references would re-render the whole thread on each tick, while
 * skipping citations entirely would swallow the update that attaches the chips
 * to an answer whose text already finished streaming.
 */
function renderedCitationsSignature(
  citations: AskPunttMessage["citations"],
): string {
  if (!citations?.length) return "";
  return citations
    .flatMap((citation) => {
      if (isKnowledgeRuleCitation(citation)) return [`rule:${citation.ruleId}`];
      if (isWebSourceCitation(citation)) {
        return [`web:${citation.sourceId}:${citation.quote ?? ""}`];
      }
      return [];
    })
    .join(",");
}

function composerContextSignature(
  quoted: AskPunttMessage["quotedContexts"],
  comment: AskPunttMessage["commentContexts"],
): string {
  const quotes = (quoted ?? []).map((ctx) => ctx.id).join(",");
  const comments = (comment ?? [])
    .map((ctx) => `${ctx.id}:${ctx.commentId}:${ctx.number}`)
    .join(",");
  return `${quotes}|${comments}`;
}

/** Exported for unit tests — keep in sync with `memo(ChatMessage, …)`. */
export function areChatMessagePropsEqual(
  prev: ChatMessageProps,
  next: ChatMessageProps,
): boolean {
  if (prev.message !== next.message) {
    // Identity preserved by streaming merge for non-active rows; if the
    // object changed, compare the fields that affect render.
    const a = prev.message;
    const b = next.message;
    if (
      a._id !== b._id ||
      a.status !== b.status ||
      a.content !== b.content ||
      a.streamingContent !== b.streamingContent ||
      a.feedback !== b.feedback ||
      a.canvasContent !== b.canvasContent ||
      a.canvasLoading !== b.canvasLoading ||
      a.viewTicketId !== b.viewTicketId ||
      a.contentBlocks !== b.contentBlocks ||
      agentStepsSignature(a.agentSteps) !== agentStepsSignature(b.agentSteps) ||
      renderedCitationsSignature(a.citations) !==
        renderedCitationsSignature(b.citations) ||
      componentsSignature(a.components) !== componentsSignature(b.components) ||
      (a.suggestedResponses?.length ?? 0) !==
        (b.suggestedResponses?.length ?? 0) ||
      (a.searchProgress?.length ?? 0) !== (b.searchProgress?.length ?? 0) ||
      (a.attachments?.length ?? 0) !== (b.attachments?.length ?? 0) ||
      composerContextSignature(a.quotedContexts, a.commentContexts) !==
        composerContextSignature(b.quotedContexts, b.commentContexts)
    ) {
      return false;
    }
  }
  return (
    prev.chatId === next.chatId &&
    prev.reviewFiles === next.reviewFiles &&
    prev.localPreviewUrls === next.localPreviewUrls &&
    prev.isStreaming === next.isStreaming &&
    prev.isLastAssistant === next.isLastAssistant &&
    prev.onSubmitAnswers === next.onSubmitAnswers &&
    prev.formDisabled === next.formDisabled &&
    prev.questionFormRef === next.questionFormRef &&
    prev.onAnswerChange === next.onAnswerChange &&
    prev.hideInlineQuestionForm === next.hideInlineQuestionForm &&
    prev.isQuestionFormAnswer === next.isQuestionFormAnswer &&
    prev.onUndoQuestionFormAnswer === next.onUndoQuestionFormAnswer &&
    prev.hideAgentChrome === next.hideAgentChrome &&
    prev.precedingUserHasAttachments === next.precedingUserHasAttachments &&
    prev.presentation === next.presentation &&
    prev.showFeedRoleLabel === next.showFeedRoleLabel &&
    prev.feedActorName === next.feedActorName &&
    prev.feedActorAvatar === next.feedActorAvatar &&
    prev.feedOwnUserMessage === next.feedOwnUserMessage &&
    prev.feedMembers === next.feedMembers &&
    prev.collapsible === next.collapsible &&
    prev.feedCompactLayout === next.feedCompactLayout &&
    prev.showAuthorHeader === next.showAuthorHeader &&
    prev.onCitationSelect === next.onCitationSelect &&
    prev.selectedCitationRuleId === next.selectedCitationRuleId
    // why: renderBeforeContent / renderAfterContent are ReactNode elements the
    // parent (AskPunttThread) rebuilds inline into a plain `rows[]` on every
    // render, so the references are never stable. Comparing them here can never
    // be true, which would defeat memoization for user messages (re-rendering
    // once per streaming token). They are cheap slot content derived from the
    // same `message` the checks above already cover, so omit them and let the
    // memo bail out on identity like assistant rows (which pass undefined).
  );
}

function ChatMessageInner({
  message,
  chatId,
  reviewFiles,
  localPreviewUrls,
  isStreaming,
  isLastAssistant,
  onSubmitAnswers,
  formDisabled,
  questionFormRef,
  onAnswerChange,
  hideInlineQuestionForm,
  isQuestionFormAnswer = false,
  onUndoQuestionFormAnswer,
  hideAgentChrome = false,
  precedingUserHasAttachments,
  presentation = "chat",
  showFeedRoleLabel = false,
  feedActorName,
  feedActorAvatar,
  feedOwnUserMessage = false,
  feedMembers,
  collapsible = false,
  feedCompactLayout = false,
  showAuthorHeader = true,
  onCitationSelect,
  selectedCitationRuleId = null,
  renderBeforeContent,
  renderAfterContent,
}: ChatMessageProps) {
  const { t } = useTranslation("ui");
  const testIds = chatMessageTestIds(message._id);
  const analytics = useAnalytics("ask_puntt_chat");
  const feedbackMutation = useSubmitAskPunttFeedback(chatId ?? "");
  const isCompleted = message.status !== "pending" && !!message.content;
  const showFeedback =
    message.role === "assistant" && isCompleted && !isStreaming && chatId;

  // Display streamingContent while content is being generated, final content once done
  // why: a reply stored before warhol stopped handing out links still carries
  // a presigned URL, the same as the steps under it (`withoutStorageLinks`).
  const storedContent = message.content || message.streamingContent || "";
  const displayContent = useMemo(
    () => redactStorageLinks(storedContent),
    [storedContent],
  );
  const hasContent = !!message.content;
  // The row is holding words the model is still writing. Deliberately NOT
  // `isStreaming`: that is a property of being the LAST row, and a follow-up
  // sent while a review runs leaves the running turn's text live but no longer
  // last — it still needs sanitizing and it still takes the writer's reset.
  const hasLiveText = !hasContent && !!message.streamingContent;
  const showStreamingCursor = isStreaming && !hasContent && hasLiveText;

  // The Knowledge Database rule citations attached by the approved_claims_kb
  // tool, in both shapes this message draws them: the deduped chips below, and
  // the same claims as links inside the prose. See the hook for why the two are
  // derived together.
  const { ruleCitations: knowledgeRuleCitations, claimLinks } =
    useMessageClaimCitations(message.citations);
  const webSourceCitations = useMemo(
    () => dedupeWebSourceCitations(message.citations ?? []),
    [message.citations],
  );

  const drawableChatAtoms = useMemo(
    () => collectDrawableChatAtoms(message.components, message.agentSteps),
    [message.components, message.agentSteps],
  );
  const hasDrawnChatAtoms =
    message.role === "assistant" && drawableChatAtoms.length > 0;

  // Append-only segment freeze: Path-2 streaming order must not reshuffle when
  // Path-1 contentBlocks arrive (see displaySegments.stabilizeDisplaySegments).
  const segmentsFreezeRef = useRef<{
    messageId: string;
    segments: ReturnType<typeof computeRawDisplaySegments>;
  }>({ messageId: "", segments: [] });

  const segments = useMemo(() => {
    const raw = computeRawDisplaySegments(
      message.agentSteps,
      displayContent,
      message.contentBlocks,
      { ignoreContentBlocks: !!isStreaming && !hasContent },
    );
    if (segmentsFreezeRef.current.messageId !== message._id) {
      segmentsFreezeRef.current = { messageId: message._id, segments: [] };
    }
    const stabilized = stabilizeDisplaySegments(
      segmentsFreezeRef.current.segments,
      raw,
      // A shorter text replaces the slot's own text while the row is live (the
      // writer's reset — narration replaced by the answer, or a rewritten
      // answer) and once it has settled (the shaped `content` is the answer,
      // and it is routinely SHORTER than the raw streamed reply: `place_markers`
      // deletes the source notes it folds into `[n]`). Off in between, i.e. on
      // a row with neither live text nor a settled answer.
      //
      // Deliberately AGENT-AGNOSTIC, where the option's own note still frames
      // the shrink as warhol's. This is the shared row — v1's `chat.tsx` and
      // `AskPunttThread` draw rembrandt turns through it — so a rembrandt row
      // takes this too, and that is the behaviour it wants. "Rembrandt
      // accumulates one growing body" is true of its BUFFER, not of the field
      // this reads: `StreamingContentWriter._get_display_content`
      // (`rembrandt …/ask_puntt/chat.py`) re-derives `streamingContent` from
      // that buffer on every 200ms flush, and the derivation SHORTENS. The tick
      // that first sees a complete ```canvas / ```brief / ```project_data /
      // ```suggested_responses opener publishes only the prose BEFORE the
      // fence, after the previous tick already published the half-typed one;
      // `strip_leaked_attachment_ids` deletes an `att_N` the model leaked
      // mid-sentence. Under the freeze the fence fragment and the leaked id
      // stayed on screen until the prose outgrew them — words rembrandt itself
      // had withdrawn. A shorter frame is the writer's newest view on both
      // agents, so neither one wants it held back.
      //
      // The one shorter frame that is NOT a newer view is warhol's size fit,
      // and it never reaches a row: `applyStreamSnapshot.liveFields` drops it
      // on `streamingContentTruncated`.
      { allowShrink: hasLiveText || hasContent },
    );
    segmentsFreezeRef.current.segments = stabilized;
    return stabilized;
  }, [
    message._id,
    message.agentSteps,
    displayContent,
    message.contentBlocks,
    isStreaming,
    hasContent,
    hasLiveText,
  ]);
  const hasSegments = segments.length > 0;
  const hasToolSegment = segments.some((segment) => segment.type === "tools");
  const hasSearchProgress = (message.searchProgress?.length ?? 0) > 0;
  const showStandaloneSearchProgress = hasSearchProgress && !hasToolSegment;
  const thinkingComplete = hasContent && !isStreaming;
  // why: leapfrog is the empty-turn placeholder. Once this assistant turn has
  // any body (tokens, tools, search, drawn atoms) the animation competes with
  // real content.
  const hasVisibleTurnContent =
    !!displayContent.trim() ||
    hasSegments ||
    hasSearchProgress ||
    hasDrawnChatAtoms;
  const showThinkingStatus =
    message.role === "assistant" &&
    !hasVisibleTurnContent &&
    !thinkingComplete &&
    (isStreaming || message.status === "pending");

  // Hidden once the turn has any body — tools/search/text own the row.
  const showLeadingThinking =
    message.role === "assistant" &&
    !!isStreaming &&
    !hasVisibleTurnContent &&
    !showStreamingCursor &&
    !showThinkingStatus;

  // Streaming cursor fade-out
  const wasStreamingRef = useRef(showStreamingCursor);
  const [cursorFading, setCursorFading] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(showStreamingCursor);

  useEffect(() => {
    if (wasStreamingRef.current && !showStreamingCursor) {
      setCursorFading(true);
      const timer = setTimeout(() => {
        setCursorVisible(false);
        setCursorFading(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (showStreamingCursor) {
      setCursorVisible(true);
      setCursorFading(false);
    }
    wasStreamingRef.current = showStreamingCursor;
  }, [showStreamingCursor]);

  // Progressive structured response reveal
  const [revealedSections, setRevealedSections] = useState(0);
  const structuredRevealedRef = useRef(false);

  useEffect(() => {
    if (
      hasContent &&
      message.structuredContent &&
      !structuredRevealedRef.current
    ) {
      structuredRevealedRef.current = true;
      const timers = [0, 150, 300, 500].map((delay, i) =>
        setTimeout(() => setRevealedSections(i + 1), delay),
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [hasContent, message.structuredContent]);

  // Resolve @tokens to actors so feed user messages render highlighted chips.
  const mentionActors = useMemo(
    () =>
      resolveMentionActorsForFeedMessage(
        message.content,
        message.mentions,
        feedMembers,
        message.mentionEmails,
      ),
    [message.content, message.mentions, message.mentionEmails, feedMembers],
  );

  // Split into text vs. attachments so the feed presentation can clamp only the
  // text while always rendering attachments in full (line-clamping an <img>
  // crops it and strands the "Show more" toggle under a sliced picture).
  const questionAnswerLabels = isQuestionFormAnswer
    ? parseQuestionFormAnswerLabels(message.content ?? "")
    : [];

  const questionFormAnswerContent =
    isQuestionFormAnswer && questionAnswerLabels.length > 0 ? (
      <div
        data-testid="chat-message-question-form-answers"
        className="flex w-full flex-col gap-2"
      >
        {questionAnswerLabels.map((label, i) => (
          <QuestionFormAnswer
            key={`${label}-${i}`}
            answer={label}
            agentComponentId={chatAtomAgentInstanceId(
              CHAT_ATOM_AGENT_COMPONENT_IDS.questionFormAnswer,
              String(i),
            )}
            onUndo={onUndoQuestionFormAnswer}
            data-testid={`chat-message-question-form-answer-${i}`}
          />
        ))}
      </div>
    ) : null;

  const userTextContent = questionFormAnswerContent ? (
    questionFormAnswerContent
  ) : mentionActors.length > 0 ? (
    <p
      data-testid="chat-message-feed-content"
      className="whitespace-pre-wrap text-base text-puntt-neutral-gray-12"
    >
      {renderMentionSegments(message.content, mentionActors).map((seg, i) =>
        seg.type === "mention" ? (
          <span
            key={i}
            data-testid={`chat-message-mention-${seg.id}`}
            className={MENTION_HIGHLIGHT_CLASS}
          >
            {seg.label}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        ),
      )}
    </p>
  ) : (
    <Markdown
      size="3"
      iconizeEmoji
      claimCitations={claimLinks}
      className="text-puntt-neutral-gray-12 [&_strong]:font-semibold [&_strong]:text-puntt-neutral-gray-12"
    >
      {message.content}
    </Markdown>
  );

  const userAttachments =
    message.attachments && message.attachments.length > 0 ? (
      <Flex direction="column" gap="2" className="mt-2">
        {message.attachments.map((att, idx) => {
          const mediaUrl =
            att.source || (att.key ? `https://static.puntt.ai/${att.key}` : "");
          return (
            <div key={att.key ?? idx} className="min-w-0">
              {att.type === "image" && mediaUrl && (
                <ImageWithLoader
                  src={(att.key && localPreviewUrls?.[att.key]) || mediaUrl}
                  alt={att.name ?? "Attachment"}
                  href={mediaUrl}
                  testId={`chat-image-attachment-${message._id}-${idx}`}
                />
              )}
              {att.type === "video" && mediaUrl && (
                <ChatVideoAttachment
                  src={
                    (att.key && localPreviewUrls?.[att.key]) ||
                    reviewFiles?.[0]?.source ||
                    mediaUrl
                  }
                  name={att.name}
                />
              )}
              {att.type === "file" && (mediaUrl || att.source) && (
                <FileAttachmentButton
                  url={mediaUrl || att.source || ""}
                  name={att.name}
                />
              )}
            </div>
          );
        })}
      </Flex>
    ) : null;

  const userContent = (
    <>
      {userTextContent}
      {userAttachments}
    </>
  );

  const feedbackButtons = showFeedback ? (
    <Flex gap="1" className="ml-1">
      <button
        data-testid="chat-feedback-thumbs-up"
        onClick={() => {
          analytics.capture("ask_puntt_feedback", {
            feedback: "thumbs_up",
            message_id: message._id,
            chat_id: chatId,
          });
          feedbackMutation.mutate({
            messageId: message._id,
            feedback: "thumbs_up",
          });
        }}
        className={`rounded-md p-1 transition-colors ${
          message.feedback === "thumbs_up"
            ? "bg-puntt-accent-3 text-puntt-accent-11"
            : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
        }`}
        aria-label={t("thumbsUp")}
      >
        <ThumbsUp
          size={16}
          weight={message.feedback === "thumbs_up" ? "fill" : "regular"}
        />
      </button>
      <button
        data-testid="chat-feedback-thumbs-down"
        onClick={() => {
          analytics.capture("ask_puntt_feedback", {
            feedback: "thumbs_down",
            message_id: message._id,
            chat_id: chatId,
          });
          feedbackMutation.mutate({
            messageId: message._id,
            feedback: "thumbs_down",
          });
        }}
        className={`rounded-md p-1 transition-colors ${
          message.feedback === "thumbs_down"
            ? "bg-puntt-red-3 text-puntt-red-9"
            : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
        }`}
        aria-label={t("thumbsDown")}
      >
        <ThumbsDown
          size={16}
          weight={message.feedback === "thumbs_down" ? "fill" : "regular"}
        />
      </button>
    </Flex>
  ) : null;

  const assistantBody = (
    <div className="min-w-0 space-y-3">
      {/* Interleaved text bubbles and tool step groups */}
      <div data-testid={testIds.content} className="space-y-3">
        {showLeadingThinking && (
          <div
            data-testid={`chat-message-leading-thinking-${message._id}`}
            className="flex items-center"
          >
            <PunttThinking size={20} label={null} />
          </div>
        )}
        {/* Startup/thinking status persists for plain assistant replies. */}
        {showThinkingStatus && !hideAgentChrome && (
          <ThinkingState
            message={message}
            precedingUserHasAttachments={precedingUserHasAttachments}
          />
        )}

        {showStandaloneSearchProgress && (
          <SearchProgress searchProgress={message.searchProgress ?? []} />
        )}

        {(() => {
          const lastToolIdx = segments.reduce(
            (acc, s, i) => (s.type === "tools" ? i : acc),
            -1,
          );
          return segments.map((segment, idx) => {
            // why-not: no `text && hasDrawnChatAtoms -> null` here -- prose is
            // the answer and the atom sits beside it (warhol rule 11). Hiding
            // text on any drawn atom blanked a review's report beside its
            // list card and the amendment beside a memory card (#5414).
            if (segment.type === "tools") {
              if (hideAgentChrome) return null;
              return (
                <div key={`tools-${idx}`} className="space-y-3">
                  <AgentSteps
                    steps={segment.steps}
                    isStreaming={
                      !!isStreaming && !hasContent && idx === lastToolIdx
                    }
                    message={message}
                  />
                  {idx === lastToolIdx &&
                    message.searchProgress &&
                    message.searchProgress.length > 0 && (
                      <SearchProgress searchProgress={message.searchProgress} />
                    )}
                </div>
              );
            }
            // Text segment
            const isLastSegment = idx === segments.length - 1;
            const showCursor =
              isLastSegment && (showStreamingCursor || cursorVisible);
            // While the trailing segment is on live text, hide what the settle
            // would delete or renumber: raw link syntax for a half-typed
            // `[label](http...`, an open source note, and a citation marker
            // (`streamingMarkdown.ts`). Gated on the ROW holding live text
            // rather than on `isStreaming`, so a follow-up that makes this row
            // no longer last does not silently drop the sanitizer. Once
            // finalized (`hasContent`), render the untouched markdown.
            const segmentContent =
              hasLiveText && isLastSegment
                ? sanitizeStreamingMarkdown(segment.content)
                : segment.content;
            // Completed segments get a memoized Markdown child so only the
            // trailing streaming segment re-parses on each tick.
            const memoizeSegment = shouldMemoizeMarkdownSegment({
              isStreaming: !!isStreaming,
              hasContent,
              isLastSegment,
            });
            return (
              <div key={`text-${idx}`} data-testid={testIds.textSegment(idx)}>
                <div className="text-puntt-neutral-gray-12">
                  {memoizeSegment ? (
                    <MemoizedMarkdownSegment
                      content={segmentContent}
                      claimCitations={claimLinks}
                      claimCitationScope={`${message._id}-${idx}`}
                    />
                  ) : (
                    <Markdown
                      size="3"
                      iconizeEmoji
                      claimCitations={claimLinks}
                      claimCitationScope={`${message._id}-${idx}`}
                    >
                      {segmentContent}
                    </Markdown>
                  )}
                  {showCursor && (
                    <span
                      className={`ml-0.5 inline-block h-4 w-[3px] animate-pulse rounded-full bg-puntt-accent-9 transition-opacity duration-200 motion-reduce:animate-none ${cursorFading ? "opacity-0" : "opacity-100"}`}
                    />
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>

      {!hasDrawnChatAtoms && feedbackButtons}

      {/* Structured response — only show when final content is set */}
      {hasContent && message.structuredContent && (
        <StructuredResponse
          content={message.structuredContent}
          maxSections={revealedSections}
          messageId={message._id}
        />
      )}

      {/* Knowledge Database rule citations — numbered chips that focus the
          Cited Claims rail (dialog / deep-link lives on the rail cards). */}
      {hasContent && knowledgeRuleCitations.length > 0 && (
        <div
          className="mt-2 flex flex-wrap items-center gap-2"
          data-testid={`chat-message-citations-${message._id}`}
        >
          <Text
            as="span"
            size="1"
            weight="medium"
            className="text-puntt-neutral-gray-11"
          >
            {t("citedClaimsPanel.title")}
          </Text>
          {knowledgeRuleCitations
            .filter((citation) => citation.related !== true)
            .map((citation, index) => {
              const n = index + 1;
              const statement = knowledgeRuleCitationLabel(
                citation,
                t("citedClaimsPanel.citedClaim"),
              );
              const selected = selectedCitationRuleId === citation.ruleId;
              const chipClass = `flex size-6 items-center justify-center rounded-full text-[11px] font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9 ${
                selected
                  ? "bg-puntt-accent-9 text-base-white"
                  : "bg-puntt-neutral-gray-4 text-puntt-neutral-gray-12 hover:bg-puntt-neutral-gray-5"
              }`;
              const chipLabel = t("citedClaimsPanel.chipAriaLabel", {
                n,
                statement,
              });
              // The number focuses the Cited Claims rail when there IS one. Only
              // the legacy chat passes `onCitationSelect`, so on the surface in
              // use this was a button whose handler was undefined -- it rendered,
              // it took a click, and nothing happened. Without a rail to focus,
              // the number does what the reader expects of a citation and opens
              // the rule it cites.
              return onCitationSelect ? (
                <button
                  key={citation.ruleId}
                  type="button"
                  data-testid={`chat-citation-chip-${message._id}-${index}`}
                  data-selected={selected ? "true" : "false"}
                  aria-label={chipLabel}
                  aria-pressed={selected}
                  onClick={() =>
                    onCitationSelect({
                      messageId: message._id,
                      ruleId: citation.ruleId,
                    })
                  }
                  className={chipClass}
                >
                  {n}
                </button>
              ) : (
                <Link
                  key={citation.ruleId}
                  data-testid={`chat-citation-chip-${message._id}-${index}`}
                  data-selected={selected ? "true" : "false"}
                  aria-label={chipLabel}
                  to={KNOWLEDGE_DATABASE_PATH}
                  search={{
                    ...KNOWLEDGE_DATABASE_DEFAULT_SEARCH,
                    tab: "rules",
                    ruleId: citation.ruleId,
                    ...(citation.sourceId
                      ? { sourceId: citation.sourceId }
                      : {}),
                    ...(citationAnchorParam(citation)
                      ? { cite: citationAnchorParam(citation) }
                      : {}),
                  }}
                  className={chipClass}
                >
                  {n}
                </Link>
              );
            })}
          {(() => {
            // The registry rows warhol filled the panel with: counted, not
            // numbered, so a chip is always a rule the answer cited.
            const related = knowledgeRuleCitations.filter(
              (citation) => citation.related === true,
            ).length;
            return related > 0 ? (
              <Text
                as="span"
                size="1"
                className="text-puntt-neutral-gray-11"
                data-testid={`chat-message-related-claims-${message._id}`}
              >
                {t("citedClaimsPanel.relatedCount", { count: related })}
              </Text>
            ) : null;
          })()}
        </div>
      )}

      {/* Verified URLs returned or opened by Warhol's native web search. */}
      {hasContent &&
        message.role === "assistant" &&
        webSourceCitations.length > 0 && (
          <div
            className="mt-2 flex flex-wrap items-center gap-2"
            data-testid={`chat-message-web-sources-${message._id}`}
          >
            <Text
              as="span"
              size="1"
              weight="medium"
              className="text-puntt-neutral-gray-11"
            >
              {t("chatMessage.webSources")}
            </Text>
            {webSourceCitations.map((citation, index) => {
              const host = new URL(citation.sourceId).hostname.replace(
                /^www\./,
                "",
              );
              return (
                <a
                  key={citation.sourceId}
                  href={citation.sourceId}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={citation.quote}
                  aria-label={`${t("chatMessage.openWebSource")}: ${host}`}
                  data-testid={`chat-web-source-${message._id}-${index}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-puntt-neutral-gray-6 bg-puntt-neutral-gray-2 px-2.5 py-1 text-xs font-medium text-puntt-neutral-gray-12 no-underline transition-colors hover:bg-puntt-neutral-gray-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-puntt-accent-9"
                >
                  <Globe
                    size={13}
                    className="text-puntt-accent-11"
                    aria-hidden
                  />
                  <span>{host}</span>
                  <ArrowSquareOut size={12} aria-hidden />
                </a>
              );
            })}
          </div>
        )}

      <AssistantAttachments
        messageId={message._id}
        attachments={message.attachments}
      />

      {/* Chat atoms the agent drew (warhol `render_component`): a card, a
          list, a form. A form takes its answer only on the last assistant
          turn, and the answer is the person's next message, in words. */}
      {hasDrawnChatAtoms && (
        <ChatAtoms
          messageId={message._id}
          atoms={drawableChatAtoms}
          reviewId={message.viewReviewId}
          interactive={!!isLastAssistant}
          disabled={formDisabled}
          onSubmitAnswers={onSubmitAnswers}
        />
      )}

      {hasDrawnChatAtoms && feedbackButtons}

      {/* Multi-question form — hidden for the active message (shown in input area); read-only for history */}
      {hasContent &&
        message.userQuestions &&
        message.userQuestions.length > 0 &&
        !hideInlineQuestionForm && (
          <QuestionForm
            data-testid="chat-message-question-form"
            ref={isLastAssistant ? questionFormRef : undefined}
            questions={message.userQuestions as AskPunttUserQuestion[]}
            onSubmit={onSubmitAnswers ?? (() => {})}
            disabled={formDisabled}
            readOnly={!isLastAssistant}
            hideSubmitButton={isLastAssistant && !!questionFormRef}
            onAnswerChange={isLastAssistant ? onAnswerChange : undefined}
          />
        )}
    </div>
  );

  // Feed presentation: a Slack-style row (left avatar + name + role +
  // timestamp) wrapping the SAME live body, so the project feed keeps full
  // fidelity (streaming, agent steps, canvas, attachments) but reads like the
  // project timeline instead of a centered chat bubble. Narrow columns use a
  // compact inline header (or omit it for consecutive same-author spans).
  if (presentation === "feed") {
    const isUser = message.role === "user";
    const name = isUser ? feedActorName || "Member" : "Puntt";
    // why: V2's Figma feed (49613:1111) is Name + time only, so the role label
    // is opt-in rather than default — see showFeedRoleLabel.
    const role = !isUser && showFeedRoleLabel ? "AI Reviewer" : undefined;
    const time = formatFeedTime(message.createdAt);
    const avatar = isUser ? (
      <AvatarWithInitials
        size={feedCompactLayout ? AVATAR_SIZE_NORMAL : AVATAR_SIZE_BIG}
        name={name}
        avatar={feedActorAvatar ?? undefined}
      />
    ) : (
      <PunttAvatar
        size={feedCompactLayout ? AVATAR_SIZE_NORMAL : AVATAR_SIZE_BIG}
      />
    );
    const body = isUser ? (
      isQuestionFormAnswer ? (
        <div className="mt-1 w-full space-y-2" data-testid={testIds.content}>
          {renderBeforeContent}
          {userTextContent}
          {userAttachments}
          {renderAfterContent}
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          {renderBeforeContent}
          <div
            className={
              feedOwnUserMessage
                ? "space-y-2 rounded-xl border border-puntt-neutral-gray-7 bg-base-white px-4 py-3 shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]"
                : "space-y-2 rounded-xl bg-puntt-neutral-gray-3 px-4 py-3"
            }
            data-testid={testIds.content}
          >
            {/* Clamp only the text; attachments always render in full below. */}
            {collapsible ? (
              <Expandable
                maxLines={4}
                size="2"
                allowCollapse
                buttonText="Show more"
                collapseButtonText="Show less"
                testId="feed-message"
              >
                {userTextContent}
              </Expandable>
            ) : (
              userTextContent
            )}
            {userAttachments}
          </div>
          {renderAfterContent}
        </div>
      )
    ) : (
      <div className="mt-1">
        {collapsible ? (
          <Expandable
            maxLines={4}
            size="2"
            allowCollapse
            buttonText="Show more"
            collapseButtonText="Show less"
            testId="feed-message"
          >
            {assistantBody}
          </Expandable>
        ) : (
          assistantBody
        )}
      </div>
    );

    if (feedCompactLayout) {
      return (
        <article
          className={
            showAuthorHeader ? "relative mb-4" : "relative mb-2 mt-0.5"
          }
          data-testid={isUser ? testIds.user : testIds.assistant}
          data-role={message.role}
          data-presentation="feed"
          data-feed-compact="true"
          data-author-header={showAuthorHeader ? "true" : "false"}
        >
          {showAuthorHeader ? (
            <div
              className="mb-1 flex min-w-0 items-center gap-2"
              data-testid="feed-message-author-header"
            >
              <div className="shrink-0">{avatar}</div>
              <span className="truncate text-sm font-semibold text-puntt-neutral-gray-12">
                {name}
              </span>
              {role && (
                <span
                  className="shrink-0 text-xs text-puntt-neutral-gray-10"
                  data-testid={testIds.feedRole}
                >
                  {role}
                </span>
              )}
              {time && (
                <Tooltip content={formatFullDateTime(message.createdAt)}>
                  <span
                    className="shrink-0 cursor-default text-xs text-puntt-neutral-gray-9"
                    data-testid="feed-message-timestamp"
                    aria-label={formatFullDateTime(message.createdAt)}
                  >
                    {time}
                  </span>
                </Tooltip>
              )}
            </div>
          ) : null}
          <div className="min-w-0">{body}</div>
        </article>
      );
    }

    return (
      <article
        className="relative mb-4 flex gap-3"
        data-testid={isUser ? testIds.user : testIds.assistant}
        data-role={message.role}
        data-presentation="feed"
      >
        <div
          className="shrink-0 pt-0.5"
          data-testid="feed-message-avatar-column"
        >
          {avatar}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-semibold text-puntt-neutral-gray-12">
              {name}
            </span>
            {role && (
              <span
                className="text-xs text-puntt-neutral-gray-10"
                data-testid={testIds.feedRole}
              >
                {role}
              </span>
            )}
            {time && (
              <Tooltip content={formatFullDateTime(message.createdAt)}>
                <span
                  className="shrink-0 cursor-default text-xs text-puntt-neutral-gray-9"
                  data-testid="feed-message-timestamp"
                  aria-label={formatFullDateTime(message.createdAt)}
                >
                  {time}
                </span>
              </Tooltip>
            )}
          </div>
          {body}
        </div>
      </article>
    );
  }

  if (message.role === "user") {
    return (
      <div
        className="relative mb-4"
        data-testid={testIds.user}
        data-role="user"
      >
        <Flex justify="end">
          {isQuestionFormAnswer ? (
            <div
              className="flex w-full max-w-[70%] flex-col items-end gap-2"
              data-testid={testIds.content}
            >
              {renderBeforeContent}
              {userContent}
              {renderAfterContent}
            </div>
          ) : (
            <div className="flex max-w-[70%] flex-col items-end gap-2">
              {renderBeforeContent}
              <div
                className="w-full space-y-2 rounded-xl border border-puntt-neutral-gray-7 bg-base-white px-4 py-3 shadow-[0_2px_3px_rgba(34,34,1,0.06),0_3px_12px_rgba(23,26,36,0.02)]"
                data-testid={testIds.content}
              >
                {userContent}
              </div>
              {renderAfterContent}
            </div>
          )}
        </Flex>
      </div>
    );
  }

  return (
    <div
      className="relative mb-4"
      data-testid={testIds.assistant}
      data-role="assistant"
    >
      {assistantBody}
    </div>
  );
}

export const ChatMessage = memo(ChatMessageInner, areChatMessagePropsEqual);
